import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { AgreementReadModel } from '@client/api';
import {
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toIntervalEndDateString,
  toIntervalStartDateString,
} from '@client/toolkit';

import { AgreementsPaths } from '../../../agreements/agreements.router';
import { useAgreementPartiesNumerationText } from '../../../agreements/hooks/use-agreement-parties-numeration-text';
import { useAgreementSpacesAdressesNumerationTexts } from '../../../agreements/hooks/use-agreement-spaces-addresses-numeration-texts';

interface AgreementItemProps {
  agreement?: AgreementReadModel;
  isFetching?: boolean;
  isBuilding?: boolean;
  isSpace?: boolean;
}

export const AgreementItem = ({ agreement, isFetching, isBuilding }: AgreementItemProps) => {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const owners = useAgreementPartiesNumerationText(agreement, 'Owner');
  const renters = useAgreementPartiesNumerationText(agreement, 'Tenant');
  const spaceAddresses = useAgreementSpacesAdressesNumerationTexts(agreement);

  const handleClick = useCallback(() => {
    if (!agreement?.id) return;

    navigate(generatePath(AgreementsPaths.Agreement, { id: agreement.id }));
  }, [agreement?.id, navigate]);

  return (
    <TableRow onClick={handleClick} hover={!!agreement?.id}>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(renters)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {renters}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!Array.isArray(spaceAddresses) || spaceAddresses.length <= 0} loading={isFetching}>
          {spaceAddresses?.map((x, idx) => (
            <span className="block mb-2 last:mb-0" key={idx}>
              {x}
            </span>
          ))}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(owners)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {owners}
        </Text>
      </TableCell>
      <TableCell loading={isFetching} className="font-bold">
        <Text isEmpty={!isDefinedString(agreement?.name)}>{agreement?.name}</Text>
      </TableCell>
      <TableCell>
        <span style={agreement?.status === 'Effective' ? { color: '#10b981' } : undefined}>
          <Text isEmpty={!isDefinedString(agreement?.status)} loading={isFetching}>
            {tType(`AgreementStatus.${agreement?.status}`)}
          </Text>
        </span>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(toIntervalStartDateString(agreement?.duration))}>
          {toIntervalStartDateString(agreement?.duration)}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(toIntervalEndDateString(agreement?.duration))}>
          {toIntervalEndDateString(agreement?.duration)}
        </Text>
      </TableCell>
    </TableRow>
  );
};
